import React, { useState, useEffect } from 'react';
import { jobAPI, Job } from '../services/api';
import './JobDetail.css';

interface JobDetailProps {
  jobId: number;
  onBack: () => void;
}

const JobDetail: React.FC<JobDetailProps> = ({ jobId, onBack }) => {
  const [job, setJob] = useState<Job | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchJobDetail = async () => {
      try {
        setLoading(true);
        setError(null);
        const jobData = await jobAPI.getJob(jobId);
        setJob(jobData);
      } catch (err) {
        setError(err instanceof Error ? err.message : '加载岗位详情失败');
      } finally {
        setLoading(false);
      }
    };

    fetchJobDetail();
  }, [jobId]);

  // 解析公司信息
  const parseCompanyInfo = (companyInfo: string) => {
    const parts = companyInfo.split('；');
    const company: any = {};
    
    parts.forEach(part => {
      if (part.includes('公司名称：')) {
        company.name = part.replace('公司名称：', '');
      } else if (part.includes('公司规模：')) {
        company.size = part.replace('公司规模：', '');
      } else if (part.includes('行业：')) {
        company.industry = part.replace('行业：', '');
      } else if (part.includes('公司简介：')) {
        company.description = part.replace('公司简介：', '');
      }
    });
    
    return company;
  };

  // 格式化创建时间
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('zh-CN', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  // 格式化其他要求（换行显示）
  const formatRequirements = (requirements: string) => {
    return requirements.split('\n').filter(req => req.trim());
  };

  if (loading) {
    return (
      <div className="job-detail-container">
        <button onClick={onBack} className="back-btn">
          ← 返回列表
        </button>
        <div className="loading">
          <div className="loading-spinner"></div>
          <p>加载中...</p>
        </div>
      </div>
    );
  }

  if (error || !job) {
    return (
      <div className="job-detail-container">
        <button onClick={onBack} className="back-btn">
          ← 返回列表
        </button>
        <div className="error">
          <h3>加载失败</h3>
          <p>{error || '岗位不存在'}</p>
        </div>
      </div>
    );
  }

  const company = parseCompanyInfo(job.company_info);

  return (
    <div className="job-detail-container">
      <button onClick={onBack} className="back-btn">
        ← 返回列表
      </button>

      <div className="job-detail">
        <div className="job-detail-header">
          <div className="job-header-content">
            <h1 className="job-title">{job.job_title}</h1>
            <div className="job-salary">{job.salary_range || '薪资面议'}</div>
          </div>
          
          <div className="job-meta-info">
            <span className="job-location">📍 {[job.country, job.state, job.city].filter(Boolean).join(' · ')}{job.work_location_detail && ` · ${job.work_location_detail}`}</span>
            {job.experience_years && (
              <span className="job-experience">💼 {job.experience_years}</span>
            )}
            {job.education_requirement && (
              <span className="job-education">🎓 {job.education_requirement}</span>
            )}
            {job.vacation_schedule && (
              <span className="job-schedule">⏰ {job.vacation_schedule}</span>
            )}
          </div>
        </div>

        <div className="job-detail-content">
          <div className="job-main-info">
            <div className="info-section">
              <h3>招聘概况</h3>
              <div className="info-grid">
                <div className="info-item">
                  <span className="info-label">招聘地区</span>
                  <span className="info-value">{[job.country, job.state, job.city].filter(Boolean).join(' · ')}</span>
                </div>
                <div className="info-item">
                  <span className="info-label">岗位要求</span>
                  <span className="info-value">
                    {job.education_requirement && `${job.education_requirement} `}
                    {job.experience_years && `| ${job.experience_years}`}
                  </span>
                </div>
                {job.work_location_detail && (
                  <div className="info-item">
                    <span className="info-label">办公地址</span>
                    <span className="info-value">{job.work_location_detail}</span>
                  </div>
                )}
                {job.vacation_schedule && (
                  <div className="info-item">
                    <span className="info-label">工作时间</span>
                    <span className="info-value">{job.vacation_schedule}</span>
                  </div>
                )}
              </div>
            </div>

            {job.other_requirements && (
              <div className="info-section">
                <h3>岗位职责与要求</h3>
                <div className="requirements-list">
                  {formatRequirements(job.other_requirements).map((req, index) => (
                    <div key={index} className="requirement-item">
                      {req}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {job.benefits_description && (
              <div className="info-section">
                <h3>福利待遇</h3>
                <div className="benefits">
                  {job.benefits_description}
                </div>
              </div>
            )}
          </div>

          <div className="job-sidebar">
            <div className="company-card">
              <div className="company-header">
                <h3>招聘方信息</h3>
                <span className="company-tag">招聘需求发布方</span>
              </div>
              <div className="company-info">
                <div className="company-name">{company.name}</div>
                
                <div className="company-details">
                  {company.industry && (
                    <div className="company-detail">
                      <span className="detail-label">所属行业</span>
                      <span className="detail-value">{company.industry}</span>
                    </div>
                  )}
                  {company.size && (
                    <div className="company-detail">
                      <span className="detail-label">公司规模</span>
                      <span className="detail-value">{company.size}</span>
                    </div>
                  )}
                </div>

                {company.description && (
                  <div className="company-description">
                    <h4>公司介绍</h4>
                    <p>{company.description}</p>
                  </div>
                )}
              </div>
            </div>
            
            <div className="job-stats-card">
              <h4>岗位统计</h4>
              <div className="stats-item">
                <span className="stats-label">当前状态</span>
                <span className={`stats-value status-${job.status === '招聘中' ? 'active' : 'closed'}`}>
                  {job.status}
                </span>
              </div>
              <div className="stats-item">
                <span className="stats-label">发布时间</span>
                <span className="stats-value">{formatDate(job.created_at)}</span>
              </div>
              {job.resume_count > 0 && (
                <div className="stats-item">
                  <span className="stats-label">收到简历</span>
                  <span className="stats-value">{job.resume_count} 份</span>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default JobDetail;
